import parse, { domToReact, HTMLReactParserOptions, Element } from "html-react-parser";

import { COMPONENTS, HEADING_VARIANTS, VARIANTS } from "@/components/common/typographyTypes";

import { Typography } from "../Typography/Typography";
import { TypographyGroup } from "../TypographyGroup/TypographyGroup";

type RichTextProps = {
  /**
   * The HTML string to be rendered. Typography components will be rendered for any elements with class tk-ty-{style-code}
   */
  html?: string | undefined;
};

/**
 * `tk-tg-` classes should only be used when content originates outside
 * front-end code, for example, when it is provided by a content author.
 *
 * Front-end consumption of typographical classes should be implemented by
 * consuming the <Typography> component.
 */
const TG_PREFIX = "tk-tg-";

const isTypographyVariant = (variant: string | undefined): variant is (typeof VARIANTS)[number] =>
  !!VARIANTS.find((v) => v === variant);

const isHeading = (heading: string | undefined): heading is (typeof HEADING_VARIANTS)[number] =>
  !!HEADING_VARIANTS.find((h) => h === heading);

const isTypographyComponent = (nodeName: string): nodeName is (typeof COMPONENTS)[number] =>
  !!COMPONENTS.find((c) => c === nodeName);

/**
 * RichText converts HTML into a `<TypographyGroup>` which contains `<Typography>` components inside.
 *
 * This means that all class and style attributes are stripped away. Custom styling is not an option.
 *
 * Therefore, all content inside of a RichText is by definition Figma design system-compliant.
 * - If your app has HTML content inside of it which content authors maintain directly, then you
 * should use `<RichText>` to present that HTML.
 * - Otherwise, for HTML-style content which developers maintain themselves, just use `<TypographyGroup>`
 * and `<Typography>` so that build-time type-safety is retained.
 */
export const RichText = /*#__PURE__*/ (function () {
  const RichText: React.FC<RichTextProps> = ({ html }) => {
    const options: HTMLReactParserOptions = {
      replace: (domNode) => {
        if (domNode instanceof Element) {
          if (domNode.attribs.class) {
            const classes = domNode.attribs.class.split(" ");
            const tgClass = classes.find(
              (className) => className.startsWith(TG_PREFIX) && className.length > TG_PREFIX.length
            );

            /*
             * Transform elements with typography classes (with limited tagName passthrough)
             */

            const maybeTypographyVariant = tgClass?.slice(TG_PREFIX.length, tgClass.length);

            if (isTypographyVariant(maybeTypographyVariant)) {
              return (
                <Typography
                  variant={maybeTypographyVariant}
                  component={isTypographyComponent(domNode.tagName) ? domNode.tagName : undefined}
                >
                  {domToReact(domNode.children, options)}
                </Typography>
              );
            }
          }

          /*
           * Fallback: transform <h1>...<h5> tags missing a typography class into appropriate heading
           */

          if (isHeading(domNode.tagName)) {
            return (
              <Typography variant={domNode.tagName}>
                {domToReact(domNode.children, options)}
              </Typography>
            );
          }

          /*
           * Fallback: transform <a> tags into external or text links
           */
          if (domNode.tagName === "a" && domNode.attribs.href) {
            return (
              <Typography
                variant="textLink"
                component={domNode.tagName}
                href={domNode.attribs.href}
                target={domNode.attribs.target}
                rel={domNode.attribs.rel}
              >
                {domToReact(domNode.children, options)}
              </Typography>
            );
          }

          /*
           * Fallback: transform <p> tag missing a typography class into B1
           */

          if (domNode.tagName === "p") {
            return (
              <Typography variant="b1" component="p">
                {domToReact(domNode.children, options)}
              </Typography>
            );
          }
        }
      },
    };

    const parsedHtml = html && parse(html, options);

    return <TypographyGroup>{parsedHtml}</TypographyGroup>;
  };

  RichText.displayName = "RichText";

  return RichText;
})();
